import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { Button } from './ui/button'
import { PageContainer } from './ui/page-container'
import { PageHeader } from './ui/page-header'
import { PageHeaderLeading } from '@/components/navigation/page-header-leading'
import { PageHeaderActions } from '@/components/navigation/page-header-actions'
import { ProfilePdfDownloadButton } from '@/components/profile/profile-pdf-download-button'
import { isProfilePdfEnabled } from '@/constants/profile-pdf'
import { useProfile, candidateSections, CandidateProfileSections, ProfileData, Experience, Education, Certification, ProfilePaper } from '../hooks/use-profile'
import { EntityDetailSkeleton } from '@/components/layout/entity-detail-skeleton'
import { MapPin, Loader2, Briefcase, AlertCircle } from 'lucide-react'
import { useArtifactCanvas } from './artifact/artifact-canvas-context'
import { PersonalInformationCanvas } from './profile/personal-information-canvas'
import { ProfileAboutCanvas } from './profile/profile-about-canvas'
import { UnifiedSkillsCanvas } from './profile/unified-skills-canvas'
import { ExperienceCanvas } from './profile/experience-canvas'
import { EducationCanvas } from './profile/education-canvas'
import { PaperCanvas } from './profile/paper-canvas'
import { CertificationCanvas } from './profile/certification-canvas'
import { InitialsAvatar } from './ui/initials-avatar'
import { getPersonDisplayMeta } from '@/lib/person-identity-display'
import { EntityMetadataRow } from '@/components/entity-metadata-row'
import {
  EntityIdentityCard,
  EntityIdentityCardBody,
  EntityIdentityCardMedia,
  EntityIdentityCardMetadata,
  EntityIdentityCardTitle,
} from '@/components/entity-identity-card'
import { EntityAboutSection } from '@/components/ui/entity-about-section'
import { PersonContactSection } from '@/components/ui/entity-contact-sections'
import { extraSocialContactFrom } from '@/lib/social-networks'
import { formatEntityLocation } from '@/utils/format-entity-location'
import { ProfileBannerCanvas } from './profile/profile-banner-canvas'
import { ProfileBannerSection } from './profile/profile-banner-section'
import { ProfileSkillsSection } from './profile/profile-section-skills-card'
import {
  ProfileExperienceSection,
  ProfilePapersSection,
  ProfileEducationSection,
  ProfileCertificationsSection,
} from './profile/profile-section-work-cards'
import { getProfileInitials } from './profile/profile-initials'
import { OpenToWorkBadge } from './profile/open-to-work-badge'
import { PublicShareLinkActions } from '@/components/public/public-share-link-actions'
import { CURRENT_CLIENT, isCoverBannerEnabled, resolveProfileFeature } from '@/constants'
import { publicProfileUrl } from '@/utils/public-share-url'
import {
  sortDemonstratedSkillSummaries,
  splitClaimedOnlySkills,
} from '@/utils/profile-skills-display'
import { formatProfileEvidenceDateRange } from '@/lib/profile-evidence-display'
import { useFocusStore } from '@/hooks/use-focus-store'


const CANDIDATE_FIELDS = { skills: true, papers: true, education: true, certifications: true }

export function Profile() {
  const {
    profile,
    isLoading,
    isError,
    isFetching,
    updateError,
    refetch,
    isUploadingBanner,
  } = useProfile()
  const candidate = candidateSections(profile)
  const { focus } = useFocusStore()
  const profileFeature = useMemo(
    () => resolveProfileFeature(focus?.current_focus),
    [focus?.current_focus],
  )
  const sections = profileFeature.sections
  const { openCanvas, closeCanvas } = useArtifactCanvas()
  const [highlightedFields, setHighlightedFields] = useState<Set<string>>(new Set())
  const [previousProfile, setPreviousProfile] = useState<ProfileData | null>(null)

  // Track profile changes and highlight differences
  React.useEffect(() => {
    if (profile && previousProfile) {
      const changedFields = new Set<string>()

      // Compare each field
      const fieldsToCompare = ['first_name', 'last_name', 'headline', 'bio', 'city', 'country', 'phone', 'website', 'linkedin_url', 'x_url', 'instagram_url', 'facebook_url', 'threads_url', 'tiktok_url', 'youtube_url', 'substack_url', 'github_url', 'extra_social_order', 'skills', 'papers', 'education', 'certifications']
      const fieldValue = (p: ProfileData, field: string) =>
        field in CANDIDATE_FIELDS
          ? candidateSections(p)[field as keyof CandidateProfileSections]
          : p[field as keyof ProfileData]
      for (const field of fieldsToCompare) {
        const oldValue = fieldValue(previousProfile, field)
        const newValue = fieldValue(profile, field)

        // Handle array comparison for skills
        if (field === 'skills') {
          const oldSkills = Array.isArray(oldValue) ? oldValue : []
          const newSkills = Array.isArray(newValue) ? newValue : []
          if (JSON.stringify([...oldSkills].sort()) !== JSON.stringify([...newSkills].sort())) {
            changedFields.add('skills')
          }
        } else if (field === 'papers' || field === 'education' || field === 'certifications') {
          if (JSON.stringify(oldValue || []) !== JSON.stringify(newValue || [])) {
            changedFields.add(field)
          }
        } else if (oldValue !== newValue) {
          changedFields.add(field)
        }
      }

      // Highlight changed fields
      if (changedFields.size > 0) {
        setHighlightedFields(changedFields)
        // Fade out highlights after 3 seconds
        setTimeout(() => {
          setHighlightedFields(new Set())
        }, 3000)
      }
    }

    // Update previous profile for next comparison
    if (profile) {
      setPreviousProfile(profile)
    }
  }, [profile, previousProfile])

  useEffect(() => {
    return () => {
      closeCanvas()
    }
  }, [closeCanvas])

  const handleOpenCoverBannerCanvas = () => {
    if (!profile) return
    openCanvas({
      title: 'Cover image',
      preventAutoFocus: true,
      content: <ProfileBannerCanvas profile={profile} onClose={closeCanvas} />,
    })
  }

  const handleOpenPersonalInformationCanvas = () => {
    if (!profile) return

      openCanvas({
      title: 'Edit professional details',
      preventAutoFocus: true,
      content: (
        <PersonalInformationCanvas
          profile={profile}
          onClose={closeCanvas}
          showHeadline={sections.headline}
          showPublicLinks={profileFeature.mode === 'full'}
          showOpenToWork={sections.openToWork}
        />
      ),
    })
  }

  const handleOpenSkillsManager = useCallback(
    (options?: { focusAdd?: boolean }) => {
      if (!profile) return

      openCanvas({
        title: options?.focusAdd ? 'Add skill' : 'Manage skills',
        preventAutoFocus: true,
        content: (
          <UnifiedSkillsCanvas
            key={options?.focusAdd ? 'profile-skills-add' : 'profile-skills-manage'}
            onClose={closeCanvas}
            focusAdd={options?.focusAdd}
          />
        ),
      })
    },
    [openCanvas, closeCanvas, profile]
  )

  const handleOpenProfileAboutCanvas = useCallback(() => {
    if (!profile) return

    openCanvas({
      title: 'About',
      preventAutoFocus: true,
      content: <ProfileAboutCanvas profile={profile} onClose={closeCanvas} />,
    })
  }, [openCanvas, closeCanvas, profile])

  const handleOpenExperienceCanvas = (experience: Experience | null) => {
    if (!profile) return

    openCanvas({
      title: experience ? 'Edit experience' : 'Add experience',
      preventAutoFocus: true,
      content: (
        <ExperienceCanvas
          experience={experience}
          onClose={closeCanvas}
        />
      ),
    })
  }

  const handleOpenPaperCanvas = (paper: ProfilePaper | null) => {
    if (!profile) return

    openCanvas({
      title: paper ? 'Edit publication' : 'Add a publication',
      preventAutoFocus: true,
      content: (
        <PaperCanvas
          paper={paper}
          onClose={closeCanvas}
        />
      ),
    })
  }

  const handleOpenEducationCanvas = (education: Education | null) => {
    if (!profile) return

    openCanvas({
      title: education ? 'Edit education' : 'Add education',
      preventAutoFocus: true,
      content: (
        <EducationCanvas
          education={education}
          onClose={closeCanvas}
        />
      ),
    })
  }

  const handleOpenCertificationCanvas = (certification: Certification | null) => {
    if (!profile) return

    openCanvas({
      title: certification ? 'Edit certification' : 'Add certification',
      preventAutoFocus: true,
      content: (
        <CertificationCanvas
          certification={certification}
          onClose={closeCanvas}
        />
      ),
    })
  }

  const allClaimedSkills = useMemo(() => candidate.skills || [], [candidate.skills])

  const demonstratedSkillSummaries = useMemo(
    () => sortDemonstratedSkillSummaries(candidate.skill_summaries ?? []),
    [candidate.skill_summaries]
  )

  const claimedOnlySkills = useMemo(
    () => splitClaimedOnlySkills(allClaimedSkills, candidate.skill_summaries),
    [allClaimedSkills, candidate.skill_summaries]
  )

  const resolvedDisplayName = useMemo((): string | null => {
    if (!profile) return null
    const formalName = getPersonDisplayMeta(profile).formalName
    return formalName || null
  }, [profile])

  const locationLine = useMemo(
    () => formatEntityLocation(profile?.city, profile?.country),
    [profile?.city, profile?.country],
  )

  if (isLoading) {
    return <EntityDetailSkeleton variant="profile" ariaLabel="Loading profile" />
  }

  if (!isLoading && isError && !profile) {
    return (
      <PageContainer>
        <div className="mx-auto w-full max-w-md rounded-lg border border-border bg-card p-[var(--sp-card)] shadow-sm">
          <div className="flex flex-col gap-4">
            <div className="flex gap-3">
              <AlertCircle className="size-10 shrink-0 text-destructive" aria-hidden />
              <div className="space-y-2 min-w-0">
                <h1 className="text-lg font-semibold text-foreground leading-snug">
                  Couldn't load your profile
                </h1>
                <p className="text-sm text-muted-foreground">
                  Check your connection and try again. If you just pulled code, run API migrations on the same
                  database your server uses.
                </p>
              </div>
            </div>
            <Button
              type="button"
              variant="default"
              onClick={() => void refetch()}
              disabled={isFetching}
              className="w-full sm:w-auto gap-2"
            >
              {isFetching ? (
                <>
                  <Loader2 className="size-4 animate-spin shrink-0" aria-hidden />
                  Retrying...
                </>
              ) : (
                'Try again'
              )}
            </Button>
          </div>
        </div>
      </PageContainer>
    )
  }

  const { colorSeed: pageColorSeed } = getProfileInitials(profile)

  const coverEnabled = isCoverBannerEnabled('profile') && sections.banner
  const publicProfilesEnabled =
    sections.publicShare &&
    (
      CURRENT_CLIENT as { features?: { publicProfiles?: { enabled?: boolean } } }
    ).features?.publicProfiles?.enabled
  const profilePdfEnabled = isProfilePdfEnabled()
  const profilePublicUrl =
    publicProfilesEnabled && profile?.public_slug ? publicProfileUrl(profile.public_slug) : ''

  return (
    <PageContainer>
      <PageHeader
        leading={<PageHeaderLeading />}
        actions={
          profilePdfEnabled || publicProfilesEnabled ? (
            <PageHeaderActions
              tertiary={
                profilePdfEnabled || publicProfilesEnabled ? (
                  <div className="flex flex-wrap items-center justify-end gap-2">
                    {profilePdfEnabled && profile?.id ? (
                      <ProfilePdfDownloadButton profileId={profile.id} />
                    ) : null}
                    {publicProfilesEnabled ? (
                      <PublicShareLinkActions url={profilePublicUrl} iconOnly />
                    ) : null}
                  </div>
                ) : undefined
              }
            />
          ) : undefined
        }
      />
        {isError && profile ? (
          <div
            className="mb-4 flex flex-col gap-3 rounded-lg border border-border bg-muted/50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
            role="status"
          >
            <p className="text-sm text-muted-foreground">
              Couldn't refresh your profile. Details below may be from an earlier load.
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void refetch()}
              disabled={isFetching}
              className="shrink-0 gap-2"
            >
              {isFetching ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
              Try again
            </Button>
          </div>
        ) : null}

        {updateError ? (
          <div className="bg-destructive/15 border border-destructive/20 rounded-lg p-4 mb-4">
            <p className="text-sm text-destructive font-medium">
              {updateError.message || 'An error occurred'}
            </p>
          </div>
        ) : null}

        {coverEnabled && profile && (
          <ProfileBannerSection
            profile={profile}
            bannerUrl={profile.banner_url}
            isUploadingBanner={isUploadingBanner}
            onEditClick={handleOpenCoverBannerCanvas}
          />
        )}

        {sections.identity ? (
        <EntityIdentityCard
          coverEnabled={coverEnabled}
          agentHighlight={profile?.id ? `profile:${profile.id}:core` : undefined}
          highlightClassName={
            highlightedFields.has('first_name') ||
            highlightedFields.has('last_name') ||
            highlightedFields.has('headline') ||
            highlightedFields.has('city') ||
            highlightedFields.has('country') ||
            highlightedFields.has('phone') ||
            highlightedFields.has('website') ||
            highlightedFields.has('linkedin_url') ||
            highlightedFields.has('x_url') ||
            highlightedFields.has('instagram_url') ||
            highlightedFields.has('facebook_url') ||
            highlightedFields.has('threads_url') ||
            highlightedFields.has('tiktok_url') ||
            highlightedFields.has('youtube_url') ||
            highlightedFields.has('substack_url') ||
            highlightedFields.has('github_url') ||
            highlightedFields.has('extra_social_order')
              ? 'bg-success/5 ring-2 ring-success/50'
              : undefined
          }
          media={
            <EntityIdentityCardMedia variant="avatar">
              <InitialsAvatar
                src={profile?.avatar_url ?? undefined}
                colorSeed={pageColorSeed}
                framed
                className="size-[120px]"
                alt=""
              />
            </EntityIdentityCardMedia>
          }
          onEdit={handleOpenPersonalInformationCanvas}
          editAriaLabel="Edit professional details"
          editTitle="Edit professional details"
          editDisabled={!profile}
        >
          <EntityIdentityCardBody>
            {resolvedDisplayName ? (
              <EntityIdentityCardTitle
                badge={
                  sections.openToWork && candidate.open_to_work ? <OpenToWorkBadge /> : null
                }
              >
                {resolvedDisplayName}
              </EntityIdentityCardTitle>
            ) : null}
            <EntityIdentityCardMetadata>
              {sections.headline && profile?.headline ? (
                <EntityMetadataRow icon={Briefcase}>
                  <span>{profile.headline}</span>
                </EntityMetadataRow>
              ) : null}
              {sections.location && locationLine ? (
                <EntityMetadataRow icon={MapPin}>
                  <span>{locationLine}</span>
                </EntityMetadataRow>
              ) : null}
              {sections.contact ? (
                <PersonContactSection
                  email={profile?.email}
                  phone={profile?.phone}
                  website={profileFeature.mode === 'full' ? profile?.website : undefined}
                  linkedin_url={profileFeature.mode === 'full' ? profile?.linkedin_url : undefined}
                  x_url={profileFeature.mode === 'full' ? profile?.x_url : undefined}
                  {...(profileFeature.mode === 'full' ? extraSocialContactFrom(profile) : {})}
                />
              ) : null}
            </EntityIdentityCardMetadata>
          </EntityIdentityCardBody>
        </EntityIdentityCard>
        ) : null}

        {sections.about ? (
        <EntityAboutSection
          body={profile?.bio}
          emptyVariant="cta"
          emptyCtaLabel="Add your bio"
          highlighted={highlightedFields.has('bio')}
          agentHighlight={profile?.id ? `profile:${profile.id}:about` : undefined}
          onEdit={handleOpenProfileAboutCanvas}
          editAriaLabel="Edit about"
          editTitle="Edit about"
        />
        ) : null}

        {sections.skills ? (
        <ProfileSkillsSection
          skillsHighlighted={highlightedFields.has('skills')}
          profileId={profile?.id}
          onManageSkills={handleOpenSkillsManager}
          skillsAddDisabled={!profile}
          demonstratedSkillSummaries={demonstratedSkillSummaries}
          claimedOnlySkills={claimedOnlySkills}
          experiences={candidate.experiences}
          education={candidate.education}
        />
        ) : null}

        {sections.experience ? (
        <ProfileExperienceSection profile={profile} onOpenExperience={handleOpenExperienceCanvas} />
        ) : null}

        {sections.papers ? (
        <ProfilePapersSection
          profile={profile}
          papersHighlighted={highlightedFields.has('papers')}
          onOpenPaper={handleOpenPaperCanvas}
        />
        ) : null}

        {sections.education ? (
        <ProfileEducationSection
          profile={profile}
          educationHighlighted={highlightedFields.has('education')}
          formatDateRange={formatProfileEvidenceDateRange}
          onOpenEducation={handleOpenEducationCanvas}
        />
        ) : null}

        {sections.certifications ? (
        <ProfileCertificationsSection
          profile={profile}
          certificationsHighlighted={highlightedFields.has('certifications')}
          onOpenCertification={handleOpenCertificationCanvas}
        />
        ) : null}

    </PageContainer>
  )
}
